<template>
  <div class="upPanel">
    <div class="upPanel__head">
      <span class="upPanel__title"><v-icon x-small class="mr-1">mdi-image-auto-adjust</v-icon>Upscale-Prüfung</span>
      <span class="upPanel__headRight">
        <v-icon
          small
          class="upPanel__eye"
          :class="{ 'upPanel__eye--on': showBoxes }"
          :title="showBoxes ? 'Regionen im Bild ausblenden' : 'Regionen im Bild einblenden'"
          @click="$emit('update:showBoxes', !showBoxes)"
        >{{ showBoxes ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
        <span v-if="scoreMin !== null" class="upPanel__score" :style="{ background: color(scoreMin) }" :title="scoreTitle">{{ scoreMin }}</span>
      </span>
    </div>

    <div v-if="error" class="upPanel__warn">{{ error }}</div>

    <!-- Stil & Inhalt: Werte immer, Abweichungen nur wenn es welche gibt -->
    <div v-if="analysis" class="upPanel__checks">
      <span class="upPanel__check" :class="{ 'upPanel__check--bad': isBad(analysis.styleScore) }">Stil <b :style="{ color: color(analysis.styleScore) }">{{ show(analysis.styleScore) }}</b></span>
      <span class="upPanel__check" :class="{ 'upPanel__check--bad': isBad(analysis.contentScore) }">Inhalt <b :style="{ color: color(analysis.contentScore) }">{{ show(analysis.contentScore) }}</b></span>
    </div>
    <ul v-if="findings.length" class="upPanel__findings">
      <li v-for="(f, i) in findings" :key="i" :class="'upPanel__finding--' + f.level">
        <span class="upPanel__tag">{{ f.tag }}</span>{{ f.text }}
      </li>
    </ul>

    <div class="upPanel__regions">
      <UpscaleRegionCard
        v-for="r in regions"
        :key="r.id"
        :region="r"
        :limit="limit"
        :enabled="layersActive"
        :hovered="hoveredId === r.id"
        @opacity="$emit('opacity', $event)"
        @focus="$emit('focus', $event)"
        @hover="$emit('hover', $event)"
      />
    </div>

    <div v-if="hasAdjustable && !layersActive" class="upPanel__muted">Die Regler wirken in der Version „Detail-Pass".</div>
    <div v-if="previewError" class="upPanel__warn">{{ previewError }}</div>
  </div>
</template>

<script>
import UpscaleRegionCard from "@/components/Upscales/UpscaleRegionCard.vue";
import { scoreColor } from "@/upscaleJobs";

/**
 * Prüfliste im LiveEditor: Task-Score, Stil- und Inhaltsbefunde, darunter je Region eine
 * kompakte Karte (Vorher/Nachher-Mischbild, Score, Deckkraft-Regler, Hinweise eingeklappt).
 */
export default {
  name: "UpscaleRegionsPanel",
  components: { UpscaleRegionCard },
  props: {
    job: { type: Object, default: null },
    /** Regionen mit Regler-Stand (upscaleRegionCards aus dem Mixin) */
    regions: { type: Array, default: () => [] },
    showBoxes: { type: Boolean, default: false },
    layersActive: { type: Boolean, default: false },
    hoveredId: { type: String, default: null },
    error: { type: String, default: "" },
    previewError: { type: String, default: "" },
  },
  computed: {
    limit() {
      return (this.job && this.job.taskScoreLimit) || 85;
    },
    analysis() {
      return (this.job && this.job.report && this.job.report.analysis) || null;
    },
    /** Niedrigster Wert — berücksichtigt, wo die Überarbeitung ausgeblendet ist */
    scoreMin() {
      const values = this.regions.map((r) => r.currentScore).filter((v) => v !== null && v !== undefined);
      const a = this.analysis || {};
      if (a.styleScore !== null && a.styleScore !== undefined) values.push(a.styleScore);
      if (a.contentScore !== null && a.contentScore !== undefined) values.push(a.contentScore);
      return values.length ? Math.min(...values) : null;
    },
    scoreTitle() {
      return `Task-Score: niedrigster Wert aus Regionen, Stil und Inhalt · Limit ${this.limit}`;
    },
    /** Was bei Stil und Inhalt abweicht (erwartete Upscale-Effekte und Rechenzeilen ausgeblendet) */
    findings() {
      const a = this.analysis;
      if (!a) return [];
      const out = [];
      const kind = { added: "Neu im Upscale", removed: "Fehlt im Upscale", changed: "Verändert" };
      for (const issue of a.contentIssues || []) {
        const real = issue.source === "detector";
        out.push({ level: real ? "major" : "note", tag: real ? "Inhalt" : "Inhalt · Hinweis", text: `${kind[issue.kind] || issue.kind}: ${issue.label}` });
      }
      for (const note of a.styleNotes || []) {
        const m = /^(Schwer|Leicht):\s*(.*)$/.exec(String(note));
        if (m) out.push({ level: m[1] === "Schwer" ? "major" : "minor", tag: m[1] === "Schwer" ? "Stil · schwer" : "Stil · leicht", text: m[2] });
      }
      return out;
    },
    hasAdjustable() {
      return this.regions.some((r) => r.adjustable);
    },
  },
  methods: {
    show(v) {
      return v === null || v === undefined ? "–" : v;
    },
    isBad(score) {
      return score !== null && score !== undefined && score < this.limit;
    },
    color(score) {
      return scoreColor(score, this.limit);
    },
  },
};
</script>

<style lang="scss" scoped>
.upPanel {
  border: 1px solid rgba(123, 92, 214, 0.3);
  border-radius: 12px;
  padding: 10px;
  font-size: 12px;
  background: #faf9fd;

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  &__title {
    font-weight: 700;
    color: #6a4fc4;
    display: flex;
    align-items: center;

    .v-icon {
      color: inherit !important;
    }
  }
  &__headRight {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__eye {
    cursor: pointer;
    color: rgba(0, 0, 0, 0.4) !important;

    &--on {
      color: #7b5cd6 !important;
    }
  }
  &__score {
    min-width: 30px;
    padding: 1px 8px;
    border-radius: 999px;
    color: #fff;
    font-weight: 800;
    font-size: 12.5px;
    text-align: center;
  }
  &__checks {
    display: flex;
    gap: 6px;
    margin-bottom: 6px;
  }
  &__check {
    padding: 2px 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 11.5px;

    &--bad {
      border-color: rgba(216, 73, 63, 0.5);
      background: #fdf1f0;
    }
  }
  &__findings {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    li {
      padding: 5px 8px;
      border-radius: 8px;
      background: #fff;
      border-left: 3px solid #d99a0b;
      font-size: 11.5px;
      line-height: 1.35;
      color: rgba(0, 0, 0, 0.75);
    }
  }
  &__finding--major {
    border-left-color: #d8493f !important;
  }
  &__finding--note {
    border-left-color: #b9b4c7 !important;
  }
  &__tag {
    display: inline-block;
    margin-right: 6px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.5);
  }
  &__regions {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__muted {
    margin-top: 6px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 11.5px;
  }
  &__warn {
    color: #a3342c;
    font-size: 11.5px;
    margin: 4px 0;
  }
}
</style>
